import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';

function AdminKPIs({ user }) {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const [kpiData, setKpiData] = useState({
    // Productivité
    total_revenue: '',
    total_deliveries: '',
    working_days: '',
    revenue_per_day: '',

    // Assiduité
    expected_days: '',
    worked_days: '',
    absences: '',
    delays: '',

    // Qualité
    complaints: '',
    damaged_packages: '',
    client_satisfaction: '',

    // Sécurité
    accidents: '',
    penalties: '',
    penalty_amount: '',

    // Esprit d'équipe
    peer_rating: '',
    helped_colleagues: '',
    team_events_attended: ''
  });

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (selectedDriver) {
      fetchDriverKPIs(selectedDriver);
    }
  }, [selectedDriver, startDate, endDate]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Erreur drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverKPIs = async (driverId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/kpis/driver/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate }
      });

      const kpis = response.data;
      setKpiData({
        total_revenue: kpis.productivity?.total_revenue || '',
        total_deliveries: kpis.productivity?.total_deliveries || '',
        working_days: kpis.productivity?.working_days || '',
        revenue_per_day: kpis.productivity?.revenue_per_day || '',

        expected_days: kpis.attendance?.expected_days || '',
        worked_days: kpis.attendance?.worked_days || '',
        absences: kpis.attendance?.absences || '',
        delays: kpis.attendance?.delays || '',

        complaints: kpis.quality?.complaints || '',
        damaged_packages: kpis.quality?.damaged_packages || '',
        client_satisfaction: kpis.quality?.client_satisfaction || '',

        accidents: kpis.safety?.accidents || '',
        penalties: kpis.safety?.penalties || '',
        penalty_amount: kpis.safety?.penalty_amount || '',

        peer_rating: kpis.teamSpirit?.peer_rating || '',
        helped_colleagues: kpis.teamSpirit?.helped_colleagues || '',
        team_events_attended: kpis.teamSpirit?.team_events_attended || ''
      });
    } catch (error) {
      console.error('Erreur KPIs:', error);
      // Reset form si pas de données
      setKpiData({
        total_revenue: '',
        total_deliveries: '',
        working_days: '',
        revenue_per_day: '',
        expected_days: '',
        worked_days: '',
        absences: '',
        delays: '',
        complaints: '',
        damaged_packages: '',
        client_satisfaction: '',
        accidents: '',
        penalties: '',
        penalty_amount: '',
        peer_rating: '',
        helped_colleagues: '',
        team_events_attended: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDriver) {
      alert('Veuillez sélectionner un chauffeur.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/kpis/driver/${selectedDriver}`, {
        start_date: startDate,
        end_date: endDate,
        productivity: {
          total_revenue: parseFloat(kpiData.total_revenue) || 0,
          total_deliveries: parseInt(kpiData.total_deliveries) || 0,
          working_days: parseInt(kpiData.working_days) || 0,
          revenue_per_day: parseFloat(kpiData.revenue_per_day) || 0
        },
        attendance: {
          expected_days: parseInt(kpiData.expected_days) || 0,
          worked_days: parseInt(kpiData.worked_days) || 0,
          absences: parseInt(kpiData.absences) || 0,
          delays: parseInt(kpiData.delays) || 0
        },
        quality: {
          complaints: parseInt(kpiData.complaints) || 0,
          damaged_packages: parseInt(kpiData.damaged_packages) || 0,
          client_satisfaction: parseFloat(kpiData.client_satisfaction) || 0
        },
        safety: {
          accidents: parseInt(kpiData.accidents) || 0,
          penalties: parseInt(kpiData.penalties) || 0,
          penalty_amount: parseFloat(kpiData.penalty_amount) || 0
        },
        teamSpirit: {
          peer_rating: parseFloat(kpiData.peer_rating) || 0,
          helped_colleagues: parseInt(kpiData.helped_colleagues) || 0,
          team_events_attended: parseInt(kpiData.team_events_attended) || 0
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('KPIs enregistrés avec succès!');
      fetchDriverKPIs(selectedDriver);
    } catch (error) {
      console.error('Erreur enregistrement:', error);
      alert('Erreur lors de l\'enregistrement des KPIs.');
    }
  };

  const handleDriverChange = (e) => {
    const driverId = parseInt(e.target.value);
    setSelectedDriver(driverId || null);
  };

  const handleInputChange = (field, value) => {
    setKpiData({ ...kpiData, [field]: value });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h2 style={{ marginBottom: '2rem' }}>Saisie des KPIs</h2>

        {/* Sélection chauffeur et période */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1.5rem',
          background: '#f8f9fa',
          borderRadius: '12px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Chauffeur
            </label>
            <select
              value={selectedDriver || ''}
              onChange={handleDriverChange}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            >
              <option value="">Sélectionner un chauffeur</option>
              {drivers.map(driver => (
                <option key={driver.user_id} value={driver.id}>
                  {driver.firstname} {driver.lastname} ({driver.vehicle_plate || 'N/A'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Date début
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Date fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
          </div>
        </div>

        {selectedDriver && (
          <form onSubmit={handleSubmit}>
            {/* Productivité */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                marginBottom: '1rem',
                padding: '0.7rem',
                background: 'linear-gradient(135deg, #B8472E 0%, #6E2A19 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                Productivité
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Chiffre d'affaires (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={kpiData.total_revenue}
                    onChange={(e) => handleInputChange('total_revenue', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Nombre de livraisons
                  </label>
                  <input
                    type="number"
                    value={kpiData.total_deliveries}
                    onChange={(e) => handleInputChange('total_deliveries', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Jours travaillés
                  </label>
                  <input
                    type="number"
                    value={kpiData.working_days}
                    onChange={(e) => handleInputChange('working_days', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    CA par jour (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={kpiData.revenue_per_day}
                    onChange={(e) => handleInputChange('revenue_per_day', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </div>

            {/* Assiduité */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                marginBottom: '1rem',
                padding: '0.7rem',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                Assiduité
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Jours attendus
                  </label>
                  <input
                    type="number"
                    value={kpiData.expected_days}
                    onChange={(e) => handleInputChange('expected_days', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Jours travaillés
                  </label>
                  <input
                    type="number"
                    value={kpiData.worked_days}
                    onChange={(e) => handleInputChange('worked_days', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Absences
                  </label>
                  <input
                    type="number"
                    value={kpiData.absences}
                    onChange={(e) => handleInputChange('absences', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Retards
                  </label>
                  <input
                    type="number"
                    value={kpiData.delays}
                    onChange={(e) => handleInputChange('delays', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </div>

            {/* Qualité */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                marginBottom: '1rem',
                padding: '0.7rem',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                Qualité
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Réclamations
                  </label>
                  <input
                    type="number"
                    value={kpiData.complaints}
                    onChange={(e) => handleInputChange('complaints', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Colis endommagés
                  </label>
                  <input
                    type="number"
                    value={kpiData.damaged_packages}
                    onChange={(e) => handleInputChange('damaged_packages', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Satisfaction client (/5)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={kpiData.client_satisfaction}
                    onChange={(e) => handleInputChange('client_satisfaction', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </div>

            {/* Sécurité */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                marginBottom: '1rem',
                padding: '0.7rem',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                Sécurité
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Accidents
                  </label>
                  <input
                    type="number"
                    value={kpiData.accidents}
                    onChange={(e) => handleInputChange('accidents', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    PV / Contraventions
                  </label>
                  <input
                    type="number"
                    value={kpiData.penalties}
                    onChange={(e) => handleInputChange('penalties', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Montant PV (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={kpiData.penalty_amount}
                    onChange={(e) => handleInputChange('penalty_amount', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </div>

            {/* Esprit d'équipe */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                marginBottom: '1rem',
                padding: '0.7rem',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                Esprit d'équipe
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Note des collègues (/5)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={kpiData.peer_rating}
                    onChange={(e) => handleInputChange('peer_rating', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Fois aidé des collègues
                  </label>
                  <input
                    type="number"
                    value={kpiData.helped_colleagues}
                    onChange={(e) => handleInputChange('helped_colleagues', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                    Événements d'équipe
                  </label>
                  <input
                    type="number"
                    value={kpiData.team_events_attended}
                    onChange={(e) => handleInputChange('team_events_attended', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedDriver(null);
                  setKpiData({
                    total_revenue: '',
                    total_deliveries: '',
                    working_days: '',
                    revenue_per_day: '',
                    expected_days: '',
                    worked_days: '',
                    absences: '',
                    delays: '',
                    complaints: '',
                    damaged_packages: '',
                    client_satisfaction: '',
                    accidents: '',
                    penalties: '',
                    penalty_amount: '',
                    peer_rating: '',
                    helped_colleagues: '',
                    team_events_attended: ''
                  });
                }}
                style={{
                  padding: '0.8rem 1.5rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                style={{
                  padding: '0.8rem 1.5rem',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Enregistrer les KPIs
              </button>
            </div>
          </form>
        )}

        {!selectedDriver && (
          <div style={{
            textAlign: 'center',
            padding: '4rem',
            color: '#999'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <p>Sélectionnez un chauffeur pour saisir ses KPIs</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminKPIs;
